import * as yup from 'yup'
import { parseGradeAndProgram } from 'utils/lmsHelpers'
import {
  getProductFromProductMap,
  isK8Grade,
  getSchoolOption,
} from 'utils/productHelpers'

// Common constants
export const specialCharactersMessaging = `Names can only include letters, numbers, and the following: '"-_/()#&*:,.`
export const BASICINPUTSCHEMA = yup
  .string()
  .trim()
  .matches(/^[\w\-\sáéíñóúüýÁÉÍÑÓÚÜÝ'"-–/()#&*:,.""]+$/, {
    message: specialCharactersMessaging,
    excludeEmptyString: true,
  })
  .required('This field is required')

// Shared URL parsing functions
export const getCurrentProductFromUrl = (pathName = '') => {
  // [LEARN-8666] TODO: if we cant find anything from the parsed name, default to middle school
  const defaultParsedProduct = 'grades-6-8'
  const parsedPathname = pathName.split('/')[2] || defaultParsedProduct

  return getProductFromProductMap(parsedPathname)
}

export const getCurrentProductFilter = (pathName = '') => {
  // [LEARN-8666] TODO: if we cant find anything from the parsed name, default to middle school
  const defaultParsedProduct = 'grades-6-8'
  const parsedPathname = pathName.split('/')[2] || defaultParsedProduct
  return parsedPathname
}

export const getFilterTypeFromPathname = (pathName = '') => {
  return getCurrentProductFromUrl(pathName)?.skuCode
}

export const getLessonsLink = (pathName = '') => {
  const currentProductName = getCurrentProductFromUrl(pathName)?.productName

  return currentProductName
    ? `/product/${currentProductName}/lessons`
    : undefined
}

// Shared filtering functions
export const filterK8Grades = (grades = []) => {
  return grades.filter(grade => isK8Grade(grade))
}

// Shared sorting functions
export const sortGradesWithKFirst = (grades = []) => {
  return grades.sort((grade1, grade2) => {
    // Check if either grade is Grade K or Kindergarten
    const isGrade1K =
      grade1.toLowerCase().includes('grade k') ||
      grade1.toLowerCase().includes('kindergarten')
    const isGrade2K =
      grade2.toLowerCase().includes('grade k') ||
      grade2.toLowerCase().includes('kindergarten')

    if (isGrade1K && !isGrade2K) {
      return -1 // Grade K comes first
    }
    if (!isGrade1K && isGrade2K) {
      return 1 // Grade K comes first
    }

    // For non-K grades, sort alphabetically
    return grade1.localeCompare(grade2)
  })
}

export const sortCurriculumTypesWithKFirst = (curriculumTypes = []) => {
  const k2Index = curriculumTypes.findIndex(
    type => type === 'k-2_curriculum_tier2',
  )
  if (k2Index !== -1) {
    const reorderedTypes = [...curriculumTypes]
    const k2Type = reorderedTypes.splice(k2Index, 1)[0]
    reorderedTypes.unshift(k2Type)
    return reorderedTypes
  }
  return curriculumTypes
}

// Schema factory function
export const createSchema = config => {
  return yup.object().shape({
    courseInstanceName: BASICINPUTSCHEMA,
    selectedCourse: yup.string().required(),
    selectedLicenseId: config.requiresLicense
      ? yup.string().required()
      : yup.string().optional(),
  })
}

// Base validator class
export class BaseValidator {
  constructor(state, props, config) {
    this.state = state
    this.props = props
    this.config = config
  }

  async schema() {
    const { selectedCourse, selectedSchool, courseInstanceName } =
      this.state || {}
    // Handle both 'id' and 'licenseId' properties from selectedSchool
    const selectedLicenseId = selectedSchool?.licenseId || selectedSchool?.id

    let selectedCourseValue
    if (this.config.validationType === 'course') {
      selectedCourseValue = selectedCourse?.name
    } else if (this.config.validationType === 'smallGroup') {
      // For small groups, if no grade is selected but school is selected, use a placeholder
      // This allows validation to pass with just school + class name
      selectedCourseValue =
        selectedCourse || (selectedSchool ? 'placeholder' : undefined)
    }

    const course = {
      selectedLicenseId,
      selectedCourse: selectedCourseValue,
      courseInstanceName,
    }

    const schema = createSchema(this.config)
    return schema.isValid(course)
  }

  async className(modalType) {
    let isValid = modalType === 'Create' ? await this.schema() : true

    const { courseInstanceName } = this.state
    const courseInstanceNameTrimmed = courseInstanceName?.trim()
    const { lms, pathName } = this.props
    const { program } = parseGradeAndProgram(pathName)

    const isDuplicateCourseName =
      lms.instructorData?.[program]?.siteEducator
        ?.map(x => x.class_title)
        ?.includes(courseInstanceNameTrimmed) || false

    let classNameErrors = { errors: null }

    if (courseInstanceNameTrimmed && courseInstanceNameTrimmed.length > 0) {
      try {
        await BASICINPUTSCHEMA.validate(courseInstanceNameTrimmed)
      } catch (e) {
        classNameErrors = e
        isValid = false
      }
    } else {
      isValid = false
    }

    if (isDuplicateCourseName) {
      classNameErrors = {
        errors: [this.config.duplicateMessage],
      }
      isValid = false
    }

    return {
      isFormValid: isValid,
      classNameErrors,
    }
  }
}

// Shared helper functions
export const getSchoolOptionFromCurrentSite = (
  currentSite,
  currentProductFilter,
  allSites,
) => {
  try {
    if (!currentSite?.siteId || !currentSite?.siteName || !allSites) {
      return null
    }

    // Find the full site data from Redux using siteId
    const fullSiteData = allSites.find(site => site.id === currentSite.siteId)

    if (!fullSiteData?.licenses) {
      return null
    }

    // Find the first valid license on the current site that matches the program type
    const targetProductCode =
      currentProductFilter === 'grades-k-5' ? 'K5P' : 'MSP'

    const validLicense = fullSiteData.licenses.find(license => {
      // Check if license is not expired and has the correct product code
      return (
        !license.isExpired &&
        license.product?.programCodes?.includes(targetProductCode)
      )
    })

    if (!validLicense) {
      return null
    }

    // Create a license-like object from the found license
    const licenseFromSite = {
      licenseId: validLicense.id.toString(),
      siteName: currentSite.siteName,
      product: validLicense.product?.description || null,
      siteId: currentSite.siteId,
    }

    const schoolOption = getSchoolOption(licenseFromSite)
    // Manually add siteId since getSchoolOption doesn't preserve it
    return {
      ...schoolOption,
      siteId: currentSite.siteId,
    }
  } catch (error) {
    console.error('Error in getSchoolOptionFromCurrentSite:', error)
    return null
  }
}

export const getInitialSelectedSchool = (
  currentSite,
  currentProductFilter,
  allSites,
) => {
  try {
    // Primary: Use currentSite if available
    const schoolFromCurrentSite = getSchoolOptionFromCurrentSite(
      currentSite,
      currentProductFilter,
      allSites,
    )
    if (schoolFromCurrentSite) {
      return schoolFromCurrentSite
    }

    // Fallback: Use existing logic
    return getSchoolOption({
      licenseId: null,
      product: null,
      siteName: null,
      grades: [],
    })
  } catch (error) {
    console.error('Error in getInitialSelectedSchool:', error)
    // Return a safe fallback
    return {
      licenseId: null,
      product: null,
      siteName: null,
      grades: [],
      siteId: null,
    }
  }
}

// Shared validation function
export const checkBlockingValidations = async (state, props, config) => {
  const { modalContent } = state || {}

  const validator = new BaseValidator(state, props, config)

  const {
    isFormValid: classNameIsValid,
    classNameErrors,
  } = await validator.className(modalContent)

  let inputErrors = {
    className: classNameErrors,
  }

  let isCreateInValid = !classNameIsValid

  // For small groups, validate that either school or grade is selected
  const { selectedSchool, selectedGrade } = state || {}
  const hasSelectedSchool =
    selectedSchool && (selectedSchool.licenseId || selectedSchool.id)
  const hasSelectedGrade = selectedGrade && selectedGrade.trim() !== ''

  isCreateInValid =
    !classNameIsValid || (!hasSelectedSchool && !hasSelectedGrade)

  return {
    isFormValid: !isCreateInValid,
    inputErrors,
  }
}

// Shared limit checking function
export const checkLimitReached = (pathName, lms, maxClasses) => {
  const currentProduct = getCurrentProductFromUrl(pathName)
  const programLmsKey = currentProduct?.lmsKeyV2
  const currentProductClasses = lms.instructorData?.[programLmsKey] ?? []
  return currentProductClasses?.length >= maxClasses
}
